import React, { useContext, useEffect, useState, useMemo } from 'react';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import PropTypes from 'prop-types';
import { useFormik, Form, FormikProvider } from 'formik';
import {
  Button,
  Box,
  Typography,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import TicketFormBasic from '../basic';
import TicketFormBatch from '../batch';
import TicketFormVoucher from '../voucher';
// contexts
import { EventContext } from '../../../../contexts/Event';
import { TicketsContext } from '../../../../contexts/Tickets';
import { useAuth } from '../../../../contexts/Auth';
import { SettingsContext } from '../../../../contexts/Settings';
// services
import { createTicket } from '../../../../services/Ticket';
import { createSection } from '../../../../services/Section';

dayjs.extend(utc);
dayjs.extend(timezone);

TicketFormCreateDialog.propTypes = {
  openTicketFormCreateDialog: PropTypes.bool.isRequired,
  setOpenTicketFormCreateDialog: PropTypes.func.isRequired,
  ticket: PropTypes.object
};

export default function TicketFormCreateDialog({
  openTicketFormCreateDialog,
  setOpenTicketFormCreateDialog,
  ticket
}) {
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);
  const { event } = useContext(EventContext);
  const { sections, setSections, setTickets } = useContext(TicketsContext);

  const initialTicketValues = useMemo(
    () => ({
      sectionId: '',
      name: '',
      description: '',
      reference: '',
      limit: '',
      price: '',
      rules: [
        {
          name: '',
          expiresIn: null,
          tmpExpiresIn: null,
          discount: '',
          limit: '',
          translation: {
            pt: {
              name: ''
            }
          }
        }
      ],
      vouchers: [
        {
          code: '',
          discount: '',
          limit: ''
        }
      ],
      translation: {
        pt: {
          name: '',
          description: ''
        }
      }
    }),
    []
  );

  const [ticketValues, setTicketValues] = useState(initialTicketValues);
  const [activeDiscountRule, setActiveDiscountRule] = useState();

  useEffect(() => {
    if (ticket?.section?._id) {
      setTicketValues({
        ...initialTicketValues,
        sectionId: ticket.section._id
      });
    } else {
      setTicketValues(initialTicketValues);
    }

    setActiveDiscountRule(undefined);
  }, [ticket, initialTicketValues]);

  // TODO: Validar data com Yup: formato e se é maior que hoje, mes tem que ser de 1 a 12, etc...
  const TicketSchema = Yup.object().shape({
    sectionId: Yup.string().when([], {
      is: () => Boolean(sections.length && !sections[0].single),
      then: Yup.string().required('Section is required')
    }),
    name: Yup.string().required('Name is required'),
    description: Yup.string(),
    reference: Yup.string().required('Reference is required'),
    limit: Yup.number().integer().positive().required('Limit is required'),
    price: Yup.number()
      .moreThan(-1, 'Price must be a positive number')
      .required('Price is required'),
    rules: Yup.array().when([], {
      is: () => activeDiscountRule === 'batch',
      then: Yup.array().of(
        Yup.object().shape({
          name: Yup.string().required('Name is required'),
          expiresIn: Yup.date().nullable().required('Expires in is required'),
          discount: Yup.number().integer().positive().required('Discount is required'),
          limit: Yup.number().integer().positive().required('Limit is required'),
          translation: Yup.object({
            pt: Yup.object({
              name: Yup.string()
            })
          })
        })
      )
    }),
    vouchers: Yup.array().when([], {
      is: () => activeDiscountRule === 'voucher',
      then: Yup.array().of(
        Yup.object().shape({
          code: Yup.string().required('Code is required'),
          discount: Yup.number().integer().positive().required('Discount is required'),
          limit: Yup.number().integer().positive().required('Limit is required')
        })
      )
    }),
    translation: Yup.object({
      pt: Yup.object({
        name: Yup.string(),
        description: Yup.string()
      })
    })
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: ticketValues,
    validationSchema: TicketSchema,
    onSubmit: async (values, actions) => {
      try {
        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };

        let section;
        if (values.sectionId) {
          section = sections.find((section) => section._id === values.sectionId);
        } else {
          const capacity = values.limit;
          const payloadSection = {
            capacity,
            event: {
              _id: event._id,
              name: event.name,
              translation: {
                pt: {
                  name: event.translation.pt.name
                }
              }
            },
            translation: {
              pt: {
                name: section?.translation?.pt?.name || '',
                description: section?.translation?.pt?.description || ''
              }
            }
          };

          section = await createSection({ headers, payload: payloadSection, eventId: event._id });
          setSections((sections) => [...sections, section]);
        }

        const payload = {
          name: values.name,
          description: values.description,
          reference: values.reference,
          limit: values.limit,
          price: values.price,
          section: {
            _id: section._id,
            name: section.name,
            description: section.description,
            single: section.single,
            capacity: section.capacity,
            translation: {
              pt: {
                name: section.translation.pt.name,
                description: section.translation.pt.description
              }
            }
          },
          event: {
            _id: event._id,
            name: event.name,
            calendar: event.calendar,
            translation: {
              pt: {
                name: event.translation.pt.name
              }
            }
          },
          rules: [],
          vouchers: [],
          translation: {
            pt: {
              name: values.translation.pt.name,
              description: values.translation.pt.description
            }
          }
        };

        if (event.bookingType === 'SEATSIO') {
          payload.section.seatsio = section.seatsio;
        }

        if (activeDiscountRule === 'batch') {
          values.rules = values.rules.map((rule) => ({
            name: rule.name,
            expiresIn: dayjs(rule.expiresIn).tz('Europe/Lisbon', true).toISOString(),
            discount: rule.discount,
            limit: rule.limit,
            translation: {
              pt: {
                name: rule.translation.pt.name
              }
            }
          }));

          payload.rules = values.rules;
        }

        if (activeDiscountRule === 'voucher') {
          payload.vouchers = values.vouchers;
        }

        const ticket = await createTicket({
          headers,
          payload,
          eventId: event._id,
          sectionId: section._id
        });

        setTickets((tickets) => {
          if (!tickets[ticket.section._id]) {
            tickets[ticket.section._id] = [];
          }

          tickets[ticket.section._id].push(ticket);

          return tickets;
        });

        handleClose();
        actions.resetForm();
        setActiveDiscountRule(undefined);
      } catch (err) {
        console.error(err);
        alert('An error occurred! Please, try again.');
      }
    }
  });

  const handleClose = () => {
    setOpenTicketFormCreateDialog(false);
  };

  const handleDiscountRuleSection = (rule) => {
    setActiveDiscountRule(rule);
  };

  const { handleSubmit, isSubmitting } = formik;

  return (
    <Dialog open={openTicketFormCreateDialog} fullWidth scroll="body" maxWidth="sm" sx={{ m: -2 }}>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogTitle sx={{ px: { xs: 2, sm: 3 } }}>Create Ticket</DialogTitle>
          <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
            <Box sx={{ mt: 1 }}>
              <TicketFormBasic formik={formik} />
            </Box>
            <Box sx={{ mt: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  <Typography
                    component="span"
                    variant="body2"
                    color="grey.500"
                    sx={{ display: 'block' }}
                  >
                    (Optional)
                  </Typography>
                  Discount Rules
                  <Typography
                    component="span"
                    variant="body2"
                    color="grey.500"
                    sx={{ display: 'block' }}
                  >
                    Add discount to your ticket based on batch rules or
                  </Typography>
                  <Typography
                    component="span"
                    variant="body2"
                    color="grey.500"
                    sx={{ display: 'block' }}
                  >
                    set vouchers to be used on this ticket only
                  </Typography>
                </Typography>

                <Stack spacing={2} direction="row" alignItems="center" justifyContent="center">
                  <Button
                    variant={activeDiscountRule === 'batch' ? 'contained' : 'outlined'}
                    size="large"
                    onClick={() => handleDiscountRuleSection('batch')}
                  >
                    Batches
                  </Button>
                  <Typography
                    component="span"
                    variant="body2"
                    color="grey.500"
                    sx={{ display: 'block', mt: 2 }}
                  >
                    or
                  </Typography>
                  <Button
                    variant={activeDiscountRule === 'voucher' ? 'contained' : 'outlined'}
                    size="large"
                    onClick={() => handleDiscountRuleSection('voucher')}
                  >
                    Vouchers
                  </Button>
                </Stack>

                <Typography
                  component="span"
                  variant="body2"
                  color="grey.500"
                  sx={{ display: 'block', mt: 2 }}
                >
                  * Never use both together
                </Typography>
              </Box>

              {activeDiscountRule === 'batch' && (
                <Box sx={{ mt: 3 }}>
                  <TicketFormBatch formik={formik} />
                </Box>
              )}
              {activeDiscountRule === 'voucher' && (
                <Box sx={{ mt: 3 }}>
                  <TicketFormVoucher formik={formik} />
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button size="large" onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton size="large" type="submit" variant="contained" loading={isSubmitting}>
              Save
            </LoadingButton>
          </DialogActions>
        </Form>
      </FormikProvider>
    </Dialog>
  );
}
